import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BarChart3,
  FileText,
  FolderTree,
  Package2,
  Truck,
  User,
  ShoppingBag,
  Users,
  Store,
  Settings,
  FileTextIcon,
  Tag,
  Gift,
  CreditCard,
  Star,
  RotateCcw,
  PackageMinus,
} from "lucide-react";
import { MdLogout, MdPointOfSale } from "react-icons/md";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOrderNotifications } from "../../context/OrderNotificationsContext";
import { useReturnsNotifications } from "../../context/ReturnsNotificationsContext";
import { FAVORITE } from "../../constants/colors";
import { Chevron } from "../CustomSVG";
import { useTranslation } from "react-i18next";
import { CategoriesProvider } from "../../context/CategoriesContext";
import CurrencySelector from "../Currency/CurrencySelector";
import { ShoppingCartCheckout } from "@mui/icons-material";
import AppLogo from "../AppLogo";

const drawerWidth = 280;

/* ------------------ Keyframes ------------------ */
const pulseAnimation = `
  @keyframes pulse {
    0%, 100% { 
      opacity: 1;
      transform: scale(1);
    }
    50% { 
      opacity: 0.6;
      transform: scale(1.05);
    }
  }
`;

const slideInAnimation = `
  @keyframes slideIn {
    from {
      transform: translateX(-20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

/* ------------------ Custom Scrollbar ------------------ */
const customScrollbar = {
  /* Webkit browsers (Chrome, Safari, Edge) */
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0,0,0,0.2)",
    borderRadius: "3px",
    transition: "background 0.3s ease",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(0,0,0,0.3)",
  },
  /* Firefox */
  scrollbarWidth: "thin" as const,
  scrollbarColor: "rgba(0,0,0,0.2) transparent",
};

/* ------------------ Drawer styles ------------------ */
// const openedMixin = (theme: Theme): CSSObject => ({
//   width: drawerWidth,
//   background: "linear-gradient(180deg, #faf7f2 0%, #f2e6db 100%)",
//   transition: theme.transitions.create("width", {
//     easing: theme.transitions.easing.easeOut,
//     duration: theme.transitions.duration.enteringScreen,
//   }),
//   overflowX: "hidden",
//   overflowY: "auto",
//   borderRight: "1px solid rgba(0,0,0,0.06)",
//   boxShadow: "4px 0 24px rgba(0,0,0,0.04)",
//   ...customScrollbar,
// });

// const closedMixin = (theme: Theme): CSSObject => ({
//   background: "linear-gradient(180deg, #faf7f2 0%, #f2e6db 100%)",
//   transition: theme.transitions.create("width", {
//     easing: theme.transitions.easing.easeIn,
//     duration: theme.transitions.duration.leavingScreen,
//   }),
//   overflowX: "hidden",
//   overflowY: "auto",
//   width: `calc(${theme.spacing(7)} + 1px)`,
//   borderRight: "1px solid rgba(0,0,0,0.06)",
//   [theme.breakpoints.up("sm")]: {
//     width: `calc(${theme.spacing(8)} + 1px)`,
//   },
//   ...customScrollbar,
// });

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1),
  minHeight: 64,
  position: "sticky",
  top: 0,
  backgroundColor: "#faf7f2",
  zIndex: 10,
  borderBottom: "1px solid rgba(0,0,0,0.06)",
}));

/* ------------------ Drawer ------------------ */
// Removed - no longer needed as we use MuiDrawer directly

/* ------------------ Logo Component ------------------ */
const LogoBox = styled(Box)(({ theme }) => ({
  width: 33,
  height: 33,
  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
  color: "#f2e6db",
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 24,
  fontWeight: 700,
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
  "&:hover": {
    transform: "translateY(-2px) scale(1.05)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
    "&::before": {
      opacity: 1,
    },
  },
  "&:active": {
    transform: "translateY(0) scale(1)",
  },
}));

/* ------------------ Floating Chevron Button ------------------ */
const DrawerChevron = styled(IconButton, {
  shouldForwardProp: (prop) =>
    prop !== "open" &&
    prop !== "isMobile" &&
    prop !== "faded" &&
    prop !== "isRtl",
})<{ open: boolean; isMobile: boolean; faded?: boolean; isRtl?: boolean }>(
  ({ theme, open, isMobile, faded, isRtl }) => ({
    position: "fixed",
    bottom: isMobile ? "24px" : "auto",
    top: isMobile ? "auto" : "32px",
    // RTL support: Place button on correct side
    ...(isMobile
      ? {
          // On mobile: bottom corner opposite to drawer
          [isRtl ? "left" : "right"]: "16px",
          [isRtl ? "right" : "left"]: "auto",
        }
      : {
          // On desktop: next to drawer edge - both sides need to be dynamic
          ...(isRtl
            ? {
                right: open ? drawerWidth - 16 : theme.spacing(7),
                left: "auto",
              }
            : {
                left: open ? drawerWidth - 16 : theme.spacing(7),
                right: "auto",
              }),
        }),
    transform: isMobile ? "none" : "translateY(-50%)",
    // width: isMobile ? 56 : 32,
    // height: isMobile ? 56 : 32,
    width: 32,
    height: 32,
    zIndex: theme.zIndex.drawer + 3,
    backgroundColor: "#faf7f2",
    border: "1px solid rgba(0,0,0,0.12)",
    boxShadow: isMobile
      ? "0 6px 20px rgba(0,0,0,0.25)"
      : "0 4px 12px rgba(0,0,0,0.15)",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    opacity: faded ? 0.3 : 1,

    // Add a subtle ring effect on mobile to make it more noticeable
    ...(isMobile &&
      !open && {
        "&::before": {
          content: '""',
          position: "absolute",
          top: -4,
          left: -4,
          right: -4,
          bottom: -4,
          borderRadius: "50%",
          border: "2px solid #1a1a1a",
          opacity: faded ? 0 : 0.2,
          animation: faded
            ? "none"
            : "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
          transition: "opacity 0.4s ease",
        },
      }),

    "&:hover": {
      backgroundColor: "#f2e6db",
      transform: isMobile ? "scale(1.05)" : "translateY(-50%) scale(1.1)",
      boxShadow: isMobile
        ? "0 8px 24px rgba(0,0,0,0.3)"
        : "0 6px 16px rgba(0,0,0,0.2)",
      opacity: 1,
    },

    "&:active": {
      transform: isMobile ? "scale(0.95)" : "translateY(-50%) scale(0.95)",
    },

    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  }),
);

/* ================== COMPONENT ================== */
export default function MiniDrawer() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { pendingOrdersCount } = useOrderNotifications();
  const { pendingReturnsCount } = useReturnsNotifications();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [desktopOpen, setDesktopOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [buttonFaded, setButtonFaded] = React.useState(false);
  const fadeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const { t, i18n } = useTranslation();

  // Auto-fade effect for mobile when drawer is closed
  React.useEffect(() => {
    if (isMobile && !mobileOpen) {
      // Clear any existing timeout
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }

      // Reset to full opacity first
      setButtonFaded(false);

      // Set timeout to fade after 15 seconds
      fadeTimeoutRef.current = setTimeout(() => {
        setButtonFaded(true);
      }, 15000);
    } else {
      // Keep button fully visible when drawer is open or on desktop
      setButtonFaded(false);
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    }

    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, [isMobile, mobileOpen]);

  // Reset fade when user interacts with the page
  React.useEffect(() => {
    const handleUserActivity = () => {
      if (isMobile && !mobileOpen && buttonFaded) {
        setButtonFaded(false);

        // Restart the fade timer
        if (fadeTimeoutRef.current) {
          clearTimeout(fadeTimeoutRef.current);
        }
        fadeTimeoutRef.current = setTimeout(() => {
          setButtonFaded(true);
        }, 15000);
      }
    };

    window.addEventListener("scroll", handleUserActivity);
    window.addEventListener("touchstart", handleUserActivity);
    window.addEventListener("mousemove", handleUserActivity);

    return () => {
      window.removeEventListener("scroll", handleUserActivity);
      window.removeEventListener("touchstart", handleUserActivity);
      window.removeEventListener("mousemove", handleUserActivity);
    };
  }, [isMobile, mobileOpen, buttonFaded]);

  const menuItems = [
    { id: 0, name: t("Dashboard"), icon: BarChart3, path: "/" },
    {
      id: 1,
      name: t("Categories"),
      icon: FolderTree,
      path: "/admin/categories",
    },
    { id: 2, name: t("Products"), icon: Package2, path: "/admin/products" },
    { id: 3, name: t("Invoices"), icon: FileTextIcon, path: "/admin/invoices" },
    { id: 4, name: t("Orders"), icon: ShoppingBag, path: "/admin/orders" },
    { id: 12, name: t("Gifts"), icon: Gift, path: "/admin/gifts" },
    {
      id: 17,
      name: t("Returns and Exchanges"),
      icon: RotateCcw,
      path: "/admin/returns",
    },
    {
      id: 5,
      name: t("Inventory"),
      icon: Store,
      path: "/admin/inventory",
    },
    {
      id: 6,
      name: t("Purchase Orders"),
      icon: ShoppingCartCheckout,
      path: "/admin/purchase-orders",
    },
    {
      id: 18,
      name: t("Purchase Returns"),
      icon: PackageMinus,
      path: "/admin/purchase-returns",
    },
    {
      id: 7,
      name: t("Point of Sale"),
      icon: MdPointOfSale,
      path: "/admin/pos",
    },
    {
      id: 8,
      name: t("Expenses"),
      icon: CreditCard,
      path: "/admin/employee-expenses",
    },
    { id: 9, name: t("Coupons"), icon: Tag, path: "/admin/coupon" },
    { id: 10, name: t("Deliveries"), icon: Truck, path: "/admin/deliveries" },
    { id: 11, name: t("Users"), icon: Users, path: "/admin/users" },
    { id: 13, name: t("Reports"), icon: FileText, path: "/admin/reports" },
    { id: 14, name: t("Ratings"), icon: Star, path: "/admin/rating" },
    { id: 15, name: t("Roles"), icon: User, path: "/admin/roles" },
    {
      id: 16,
      name: t("Settings"),
      icon: Settings,
      path: "/admin/company-settings",
    },
  ];

  const logoutItem = {
    id: 99,
    name: t("Log out"),
    icon: MdLogout,
    action: () => {
      logout();
      navigate("/");
    },
    danger: true,
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const handleDrawerClose = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const changeLanguage = (lang: "en" | "ar") => {
    i18n.changeLanguage(lang);
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  };

  const MenuItem = ({ item, index }: any) => {
    const Icon = item.icon;
    const isSelected = item.path ? location.pathname === item.path : false;
    const isDrawerOpen = isMobile ? mobileOpen : desktopOpen;
    const showOrdersBadge =
      item.path === "/admin/orders" && pendingOrdersCount > 0;
    const showReturnsBadge =
      item.path === "/admin/returns" && pendingReturnsCount > 0;
    const badgeCount = showOrdersBadge
      ? pendingOrdersCount
      : showReturnsBadge
        ? pendingReturnsCount
        : 0;

    const button = (
      <ListItemButton
        selected={isSelected}
        onClick={() => {
          item.path ? navigate(item.path) : item.action?.();
          handleDrawerClose();
        }}
        sx={{
          minHeight: 44,
          justifyContent: isDrawerOpen ? "initial" : "center",
          px: 2.5,
          my: 0,
          mx: 1,
          borderRadius: 2,
          color: item.danger ? FAVORITE : "#2d2d2d",
          position: "relative",
          overflow: showOrdersBadge || showReturnsBadge ? "visible" : "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: 4,
            height: 0,
            backgroundColor: item.danger ? FAVORITE : "#1a1a1a",
            borderRadius: "0 4px 4px 0",
            transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          },
          "&.Mui-selected": {
            backgroundColor: item.danger
              ? "rgba(255,0,0,0.08)"
              : "rgba(0,0,0,0.06)",
            color: item.danger ? FAVORITE : "#1a1a1a",
            fontWeight: 600,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            "&::before": {
              height: 32,
            },
            "&:hover": {
              backgroundColor: item.danger
                ? "rgba(255,0,0,0.12)"
                : "rgba(0,0,0,0.08)",
            },
          },
          "&:hover": {
            backgroundColor: item.danger
              ? "rgba(255,0,0,0.06)"
              : "rgba(0,0,0,0.04)",
            transform: "translateX(4px)",
            "&::before": {
              height: isSelected ? 26 : 12,
            },
          },
          "&:active": {
            transform: "translateX(2px) scale(0.98)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 32,
            transition: "transform 0.3s ease",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          {showOrdersBadge || showReturnsBadge ? (
            <Badge
              badgeContent={badgeCount}
              color="error"
              max={99}
              overlap="circular"
              aria-label={`${badgeCount} pending requests`}
            >
              <Icon size={22} />
            </Badge>
          ) : (
            <Icon size={22} />
          )}
        </Box>
        {isDrawerOpen && (
          <ListItemText
            sx={{
              ml: 2,
              "& .MuiTypography-root": {
                fontWeight: isSelected ? 600 : 500,
                fontSize: "0.95rem",
                letterSpacing: "0.01em",
              },
            }}
            primary={item.name}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem disablePadding>
        {!isDrawerOpen && !isMobile ? (
          <Tooltip
            title={item.name}
            placement="right"
            arrow
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, 8],
                    },
                  },
                ],
              },
            }}
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </ListItem>
    );
  };

  const drawerContent = (
    <>
      <DrawerHeader className={!isMobile && !desktopOpen ? "mt-2" : "mt-8"}>
        <AppLogo
          className={
            !isMobile && !desktopOpen ? "w-12 h-12" : "w-36 h-36 mx-8 mb-8"
          }
        />
      </DrawerHeader>

      {mobileOpen || desktopOpen ? (
        <button
          className="flex flex-row text-black font-bold my-1 ml-8"
          style={{ animation: "pulse 2s ease-in-out infinite", zIndex: 999 }}
          onClick={() => {
            // console.log("profile clicked");
            navigate("account/profile");
          }}
        >
          <User className="w-6 h-6 text-green-700 font-bold mr-1" />
          {t("welcome")} {user?.firstName || "User"}!
        </button>
      ) : null}

      <Divider sx={{ borderColor: "rgba(0,0,0,0.08)" }} />

      <List sx={{ px: 0.5, pt: 1 }}>
        {menuItems.map((item, index) => (
          <MenuItem key={item.id} item={item} index={index} />
        ))}
      </List>

      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ borderColor: "rgba(0,0,0,0.08)", mx: 2, mb: 1 }} />

      <List sx={{ px: 0.5, pb: 2 }}>
        {(isMobile ? mobileOpen : desktopOpen) && (
          <div className="mt-1 px-6 space-y-2">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Language / اللغة
              </label>
              <select
                className="language-select w-full"
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value as "en" | "ar")}
              >
                <option value="en">English</option>
                <option value="ar">العربية</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                {t("Currency")}
              </label>
              <CurrencySelector className="w-full" />
            </div>
          </div>
        )}
        <MenuItem item={logoutItem} index={menuItems.length} />
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <style>{pulseAnimation}</style>
      <style>{slideInAnimation}</style>

      {/* Floating Chevron Button - Works on both mobile and desktop */}
      <DrawerChevron
        open={isMobile ? mobileOpen : desktopOpen}
        isMobile={isMobile}
        faded={buttonFaded}
        isRtl={i18n.language === "ar"}
        onClick={handleDrawerToggle}
      >
        <Chevron isLeft={isMobile ? mobileOpen : desktopOpen} />
      </DrawerChevron>

      {/* Mobile Drawer */}
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              background: "linear-gradient(180deg, #faf7f2 0%, #f2e6db 100%)",
              boxSizing: "border-box",
              ...customScrollbar,
            },
            "& .MuiBackdrop-root": {
              backgroundColor: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
            },
          }}
          anchor={i18n.language === "ar" ? "right" : "left"}
        >
          {drawerContent}
        </MuiDrawer>
      ) : (
        /* Desktop Drawer - Always on left for LTR, right for RTL */
        <MuiDrawer
          variant="permanent"
          open={desktopOpen}
          anchor={i18n.language === "ar" ? "right" : "left"}
          sx={{
            display: { xs: "none", md: "block" },
            width: 0,
            flexShrink: 0,
            whiteSpace: "nowrap",
            "& .MuiDrawer-paper": {
              width: desktopOpen
                ? drawerWidth
                : `calc(${theme.spacing(8)} + 1px)`,
              background: "linear-gradient(180deg, #faf7f2 0%, #f2e6db 100%)",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: "hidden",
              overflowY: "auto",
              ...(i18n.language === "ar"
                ? {
                    borderLeft: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "-4px 0 24px rgba(0,0,0,0.04)",
                  }
                : {
                    borderRight: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "4px 0 24px rgba(0,0,0,0.04)",
                  }),
              ...customScrollbar,
            },
          }}
        >
          {drawerContent}
        </MuiDrawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: "100vh",
          backgroundColor: "#fafafa",
          transition: theme.transitions.create(["margin", "padding"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          pt: 0,
          pb: 0,
          // Desktop: margin for drawer
          ...(!isMobile && {
            [i18n.language === "ar" ? "marginRight" : "marginLeft"]: desktopOpen
              ? `${drawerWidth}px`
              : `calc(${theme.spacing(7)} + 1px)`,
            [i18n.language === "ar" ? "marginLeft" : "marginRight"]: 0,
          }),
          // Mobile: padding for floating button
          ...(isMobile && {
            [i18n.language === "ar" ? "pl" : "pr"]: 0,
            [i18n.language === "ar" ? "pr" : "pl"]: 0,
          }),
        }}
      >
        <CategoriesProvider>
          <Outlet />
        </CategoriesProvider>
      </Box>
    </Box>
  );
}
